import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardContent, CardGuide, CardHeader, CardHeadline } from "../../Card";
import { BarAreaStacked } from "../../Charts";
import { SetBarNormal, SetBarHovered, SetBarSelected } from "../../Charts/BarAreaStacked";
import { ContentHeader, ContentHeadline } from "../../Content";
import { GET_COWS_LIST_REQUEST, HANDLE_FILTER_COW_LIST_INITIALIZE_LOCAL, HANDLE_COW_LIST_SELECT, HANDLE_COW_LIST_HOVERED } from "../../redux/CowsList/constants";
import { SELECT_DATA_TME_PERIOD, SELECT_LACTATION_FILTERS } from "../../redux/Filters/constants";
import { IFilterState } from "../../redux/Filters/reducer";
import { handleTriggerStartTour } from "../../utills/intercom";
import { OPEN_COW_LIST, SET_LIBRARY_OPEN_URL } from "../../redux/Navigation/constants";
import { GET_PERFORMANCE_SCORE_HISTORY_REQUEST } from "../../redux/PerformanceScoreHistory/constants";
import KPIPageFilters from "../../KPIPageFilters";
import { Column, Row } from "../../Layout";
import OverView12Month from "../../Cards/OverView12Month"
import { contrastedTwoColorPalette } from "../../styles/ColorCodes";
import { BarExtendedDatum } from "@nivo/bar";

const SEA: React.FC = (props) => {

    const filtersState: IFilterState = useSelector((state: any) => state.filtersState);
    const cowsListState = useSelector((state: any) => state.cowsListState);
    const performanceScoreHistoryState = useSelector((state: any) => state.performanceScoreHistoryState);
    const [secondaryExpandedState, handleToggleSecondaryExpandedState] = useState(false);

    const dispatch = useDispatch();

    const keys = ["DaysFromCalving", "SEAState"];
    const filters: any = {};

    useEffect(() => {
        const startDate = new Date(filtersState.today);
        startDate.setDate(startDate.getDate() - filtersState.anoestrusLengthSelectedTimePeriod.days);
        filtersState.lactationFilters.culledChecked = filtersState.lactationFilters.l1Checked = filtersState.lactationFilters.l2Checked = filtersState.lactationFilters.l3Checked = true;

        // get the cowlist
        dispatch({
            type: GET_COWS_LIST_REQUEST,
            payload: {
                filters,
                keys,
                path: "sea",
                startDate: startDate,
                stopDate: filtersState.today,
                lactationFilters: filtersState.lactationFilters,
            },
        });

        dispatch({ type: GET_PERFORMANCE_SCORE_HISTORY_REQUEST, payload: { kpi: "SEA" } });
    }, [dispatch, filtersState.anoestrusLengthSelectedTimePeriod, filtersState.today]);

    const handleSelectTimePeriod = (e: any, key: string) => dispatch({ type: SELECT_DATA_TME_PERIOD, payload: { index: e.target.selectedIndex, key } });

    const handleToggleLactationFilter = (key: string) => {
        SetBarNormal(cowsListState.selected)
        dispatch({ type: HANDLE_COW_LIST_SELECT, payload: undefined });

        dispatch({ type: SELECT_LACTATION_FILTERS, payload: { key } });
        const newFilter = filtersState.lactationFilters;
        newFilter[`${key}Checked`] = !newFilter[`${key}Checked`];
        dispatch({
            type: HANDLE_FILTER_COW_LIST_INITIALIZE_LOCAL,
            payload: {
                filters,
                keys,
                path: "sea",
                data: cowsListState.data,
                lactationFilters: newFilter,
                p2reference: cowsListState.p2reference,
                p3reference: cowsListState.p3reference,
            },
        });
    }

    const handleSetCowListFilter = (filters: any, keys: string[]) => {
        dispatch({
            type: HANDLE_FILTER_COW_LIST_INITIALIZE_LOCAL,
            payload: {
                filters: (cowsListState.selected === cowsListState.hovered ? {} : filters),
                keys,
                path: "sea",
                data: cowsListState.data,
                lactationFilters: filtersState.lactationFilters,
                p2reference: cowsListState.p2reference,
                p3reference: cowsListState.p3reference,
            },
        });
    }

    const handleShowCows = (elem: any) => {
        if (cowsListState.selected !== cowsListState.hovered) {
            if (cowsListState.hovered !== undefined) {
                // set selected
                SetBarSelected(cowsListState.hovered);
            }
            if (cowsListState.selected !== undefined) {
                // clear selected
                SetBarNormal(cowsListState.selected);
            }
            dispatch({ type: HANDLE_COW_LIST_SELECT, payload: cowsListState.hovered });
        } else {
            if (cowsListState.selected !== undefined) {
                // de-select selected, so set hovered
                SetBarHovered(cowsListState.selected)
            }
            dispatch({ type: HANDLE_COW_LIST_SELECT, payload: undefined });
        }

        dispatch({ type: OPEN_COW_LIST });
    }

    const handleMouseEnter = (elem: BarExtendedDatum, event: React.MouseEvent) => {
        dispatch({ type: HANDLE_COW_LIST_HOVERED, payload: event.currentTarget });
    };

    const handleSetUrl = (url: string) => dispatch({ type: SET_LIBRARY_OPEN_URL, payload: url });

    return (
        <Fragment>
            <Row first={true} flex={1}>
                <Column flex={3} first={true}>
                    <ContentHeader>
                        <ContentHeadline text="Embryo Attachment" subHeader="Evaluates the number of Successful Inseminations (25DFAI) which result in a Successful Embryo Attachment based on progesterone level on 35DFAI (absence of early embryo death)" />
                        <KPIPageFilters handleSelectTimePeriod={handleSelectTimePeriod} filtersState={filtersState} handleToggleLactationFilter={handleToggleLactationFilter} />
                    </ContentHeader>
                </Column>
                <Column flex={2}>
                    <OverView12Month
                        performanceScore={performanceScoreHistoryState}
                        stepGuide={178811}
                        kpi={"sea"}
                    />
                </Column>
            </Row>
            <Row last={true} flex={3}>
                <Column first={true} flex={secondaryExpandedState ? 1 : 3}>
                    <Card>
                        <CardHeader>
                            <CardHeadline text={`Embryo attachement and occurrence of early embryo death`} />
                            <CardGuide
                                onOpenLib={() => handleSetUrl("sea/lvl3/eaaooeed/summary")}
                                openStepGuide={() => handleTriggerStartTour(178806)}
                                toggleExpand={() => handleToggleSecondaryExpandedState(!secondaryExpandedState)}
                                isExpanded={!secondaryExpandedState}
                            />
                        </CardHeader>
                        <CardContent>
                            <BarAreaStacked
                                filter={(filters: any) => handleSetCowListFilter(filters, ["DaysFromCalving", "SEAState"])}
                                filterKeys={[{ key: "SEAState", value: "id" }, { key: "ranges", value: "indexValue" }]}
                                tooltipHeadline={`Embryo Attachment [Days in Milk]`}
                                data={cowsListState.graphP2Data}
                                keys={["Successful", "Early embryo death"]}
                                indexBy="Bin"
                                yLegend="Number of cows"
                                xLegend="DIM on insemination"
                                colors={contrastedTwoColorPalette}
                                maxValue={cowsListState.graphP2Max}
                                onClick={(elem: any) => handleShowCows(elem)}
                                onMouseEnter={(elem: BarExtendedDatum, event: React.MouseEvent) => handleMouseEnter(elem, event)}
                                selected={cowsListState.selected}
                            />
                        </CardContent>
                    </Card>
                </Column>
                <Column last={true} flex={secondaryExpandedState ? 3 : 1}>
                    <Card>
                        <CardHeader>
                            <CardHeadline text="Final establishment of pregnancy" />
                            <CardGuide
                                onOpenLib={() => handleSetUrl("pbss/lvl3/feopaooea/summary")}
                                openStepGuide={() => handleTriggerStartTour(178810)}
                                toggleExpand={() => handleToggleSecondaryExpandedState(!secondaryExpandedState)}
                                isExpanded={secondaryExpandedState}
                            />
                        </CardHeader>
                        <CardContent>
                            <BarAreaStacked
                                filter={(filters: any) => handleSetCowListFilter(filters, ["DaysFromCalving", "PBSSState"])}
                                filterKeys={[{ key: "PBSSState", value: "id" }, { key: "ranges", value: "indexValue" }]}
                                tooltipHeadline={`Final Establishment of Pregnancy`}
                                data={cowsListState.graphP3Data}
                                keys={["Successful", "Early abortion"]}
                                indexBy="Bin"
                                yLegend="Number of cows"
                                xLegend="DIM on insemination"
                                maxValue={cowsListState.graphP3Max}
                                colors={contrastedTwoColorPalette}
                                onClick={(elem: any) => handleShowCows(elem)}
                                onMouseEnter={(elem: BarExtendedDatum, event: React.MouseEvent) => handleMouseEnter(elem, event)}
                                selected={cowsListState.selected}
                            />
                        </CardContent>
                    </Card>
                </Column>
            </Row>
        </Fragment>
    );
};

export default SEA;
